import { useEffect, useState } from "react";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Wrench, Flask, ArrowRight, ShieldCheck, ShieldWarning, Warning, X } from "@phosphor-icons/react";
import { ScoreInfo } from "@/components/mai/Explain";
import { AskMAI } from "@/components/mai/AskMAI";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BAND_COLOR = { "Launch Ready": "#047857", "Advanced": "#1D4ED8", "Developing": "#B45309", "Foundational": "#B91C1C" };
const VERDICT_META = [
  { key: 1, label: "Submit Today", icon: CheckCircle, color: "#047857" },
  { key: 2, label: "Minor Localization", icon: Wrench, color: "#B45309" },
  { key: 3, label: "Evidence Required", icon: Flask, color: "#B91C1C" },
];

// Prompt 3: new domain-readiness verdict palette
const VERDICT_STATUS = {
  verified_submission_ready: { color: "#047857", icon: ShieldCheck, short: "Verified" },
  conditionally_ready:       { color: "#1D4ED8", icon: CheckCircle, short: "Conditional" },
  local_adaptation_required: { color: "#B45309", icon: Wrench,      short: "Local adapt." },
  blocked_by_mandatory:      { color: "#B91C1C", icon: Warning,     short: "Blocked" },
};

const DomainReadinessSection = ({ productId }) => {
  const [dom, setDom] = useState(null);
  const [expanded, setExpanded] = useState(null); // country_id
  useEffect(() => {
    axios.get(`${API}/readiness/${productId}/domains`).then(r => setDom(r.data)).catch(console.error);
  }, [productId]);
  if (!dom) return <div data-testid="domain-readiness-loading" className="text-center text-slate-400 text-xs py-8">Loading domain readiness…</div>;
  const s = dom.summary;
  const buckets = [
    ["verified_submission_ready", "Verified submission ready", s.verified_submission_ready],
    ["conditionally_ready",       "Evidence package may support pathway initiation", s.conditionally_ready],
    ["local_adaptation_required", "Local adaptation required", s.local_adaptation_required],
    ["blocked_by_mandatory",      "Blocked by a mandatory requirement", s.blocked_by_mandatory],
  ];
  return (
    <section data-testid="domain-readiness-section" className="bg-white border border-slate-200 mb-8">
      <div className="px-5 py-4 border-b border-slate-200">
        <p className="font-heading font-bold text-sm tracking-tight">Domain Readiness &amp; Mandatory Gates</p>
        <p className="text-xs text-slate-500 mt-1">
          Every country carries eight domain scores plus mandatory-gate results. A failed mandatory gate blocks the country from a
          <em> Verified submission ready</em> label regardless of the domain average.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 divide-x divide-slate-100">
        {buckets.map(([code, label, n]) => {
          const meta = VERDICT_STATUS[code];
          return (
            <div key={code} data-testid={`domain-bucket-${code}`} className="px-5 py-4">
              <div className="flex items-center gap-1.5 mb-1">
                <meta.icon size={13} weight="bold" style={{ color: meta.color }} />
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{meta.short}</span>
              </div>
              <p className="font-mono-data text-2xl font-bold" style={{ color: meta.color }}>{n}</p>
              <p className="text-[11px] text-slate-500 mt-1 leading-tight">{label}</p>
            </div>
          );
        })}
      </div>
      <div className="border-t border-slate-100 divide-y divide-slate-50">
        {dom.countries.map((c) => {
          const meta = VERDICT_STATUS[c.verdict_code];
          const isOpen = expanded === c.id;
          return (
            <div key={c.id} data-testid={`domain-country-${c.id}`}>
              <button onClick={() => setExpanded(isOpen ? null : c.id)}
                      data-testid={`domain-country-${c.id}-toggle`}
                      className="w-full px-5 py-2.5 flex items-center gap-4 text-left hover:bg-slate-50">
                <span className="text-lg leading-none">{c.flag}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-semibold text-slate-800 truncate">{c.name}</p>
                    <span className="text-[10px] px-1.5 py-0.5 border font-bold uppercase tracking-wider"
                          style={{ color: meta.color, borderColor: meta.color }}>{meta.short}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5 truncate">
                    {c.verdict_label}
                    {c.blocking_gate ? ` · BLOCKING: ${c.blocking_gate.label}` : ""}
                  </p>
                </div>
                <span className="font-mono-data text-xs font-bold" style={{ color: meta.color }}>{c.domain_average}</span>
                <span className="text-slate-400 text-[10px]">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen && (
                <div data-testid={`domain-country-${c.id}-detail`} className="bg-slate-50 border-t border-slate-100 px-5 py-4">
                  {c.blocking_gate && (
                    <div data-testid={`blocking-gate-${c.id}`}
                         className="border-l-4 mb-3 pl-3 py-2 bg-rose-50" style={{ borderColor: "#B91C1C" }}>
                      <p className="text-[11px] font-bold text-rose-800 uppercase tracking-wider">Blocking Gate</p>
                      <p className="text-xs text-rose-900 mt-0.5">{c.blocking_gate.label}</p>
                      <p className="text-[11px] text-rose-700 mt-0.5">{c.blocking_gate.detail}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                    {Object.entries(c.domain_scores).map(([did, ds]) => {
                      const domainLabel = dom.domains.find(x => x.id === did)?.label || did;
                      return (
                        <div key={did} data-testid={`domain-row-${c.id}-${did}`}>
                          <div className="flex items-center justify-between">
                            <p className="text-[11px] font-semibold text-slate-700">{domainLabel}</p>
                            <p className="font-mono-data text-[11px] font-bold text-slate-800">{ds.score}</p>
                          </div>
                          <div className="h-1.5 bg-slate-100 mt-1">
                            <div className="h-full" style={{ width: `${ds.score}%`, background: ds.score >= 70 ? "#047857" : ds.score >= 50 ? "#B45309" : "#B91C1C" }} />
                          </div>
                          {ds.signals[0] && <p className="text-[10px] text-slate-500 mt-1 leading-tight">{ds.signals[0]}</p>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-200">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Mandatory Gates</p>
                    <div className="space-y-1">
                      {c.gates.map((g) => (
                        <div key={g.gate} className="flex items-start gap-2 text-[11px]">
                          {g.status === "passed" ? <ShieldCheck size={12} className="mt-0.5 shrink-0" color="#047857" /> :
                           g.status === "failed" ? <Warning size={12} className="mt-0.5 shrink-0" color="#B91C1C" /> :
                                                   <ShieldWarning size={12} className="mt-0.5 shrink-0" color="#94A3B8" />}
                          <div>
                            <span className={g.status === "failed" ? "text-rose-800 font-semibold" : "text-slate-700"}>{g.label}</span>
                            <span className="text-slate-400"> — {g.detail}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default function ReadinessPage() {
  const { productId, product } = useProduct();
  const [d, setD] = useState(null);
  const [asOf, setAsOf] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setD(null); setAsOf(null);
    axios.get(`${API}/readiness/${productId}`).then((r) => setD(r.data)).catch(console.error);
    axios.get(`${API}/score-history/${productId}`).then((r) => setAsOf(r.data.as_of)).catch(() => {});
  }, [productId]);

  if (!d) return (
    <PageShell pageId="readiness" overline="Global Market Access Readiness Engine" title="Are we Market Access Ready?">
      <div data-testid="loading-state" className="py-32 text-center text-slate-400 text-sm">Computing GMARI™…</div>
    </PageShell>
  );

  const g = d.gmari;
  // ONE primary GMARI: verified replaces provisional only once verification data exists.
  const verifiedReady = g.final_basis === "verified" && (g.verified_asset_count ?? 0) > 0;
  const finalScore = verifiedReady ? (g.final_score ?? g.score) : (g.provisional_score ?? g.score);
  const finalBand = verifiedReady ? (g.final_band ?? g.band) : (g.provisional_band ?? g.band);
  const color = BAND_COLOR[finalBand] || BAND_COLOR[g.band];
  return (
    <PageShell pageId="readiness" overline={`Global Market Access Readiness Engine · ${d.product.name}`} title="Are we Market Access Ready?">
      <AskMAI countries={d.country_readiness} />
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
        <div data-testid="gmari-card" className="bg-slate-900 text-white p-8 flex flex-col justify-between">
          <div>
            <p className="overline-label text-white/50">{g.trademark} · Global Market Access Readiness Index <ScoreInfo scoreId="gmari" light /></p>
            <p className="font-heading font-extrabold leading-none mt-4" style={{ fontSize: "84px" }}>
              {finalScore}<span className="text-2xl text-white/40">/100</span>
            </p>
            <span data-testid="gmari-band" className="inline-block mt-3 text-[11px] uppercase tracking-widest font-bold px-3 py-1.5" style={{ background: color }}>{finalBand}</span>
            {!verifiedReady && (
              <span data-testid="gmari-provisional-chip" className="inline-block mt-3 ml-2 text-[11px] uppercase tracking-widest font-bold px-3 py-1.5 border border-white/30 text-white/70">Provisional</span>
            )}
            {verifiedReady ? (
              <p data-testid="gmari-verified-basis" className="font-mono-data text-[10px] text-white/60 mt-3">
                Final conclusion — verified evidence only ({g.verified_asset_count ?? 0} of {g.total_asset_count ?? "—"} records verified).
                Provisional GMARI: {g.provisional_score} ({g.provisional_band}).
              </p>
            ) : (
              <div data-testid="gmari-verification-status" className="mt-4 border border-white/15 px-3.5 py-2.5">
                <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Evidence verification</p>
                <p className="font-mono-data text-[11px] text-white/70 mt-1">
                  {g.verified_asset_count ?? 0} of {g.total_asset_count ?? "—"} evidence records verified
                </p>
                <p className="font-mono-data text-[11px] text-white/50">Verified GMARI: Not yet available</p>
              </div>
            )}
            {asOf && <p data-testid="readiness-as-of" className="font-mono-data text-[10px] text-white/40 mt-3">All readiness scores as of {new Date(asOf).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })} — the most recent evidence or market data change.</p>}
          </div>
          <p className="text-xs text-white/50 leading-relaxed mt-6">{g.verified_definition || g.definition}</p>
        </div>
        <div data-testid="gmari-dimensions" className="xl:col-span-2 bg-white border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-200">
            <p className="font-heading font-bold text-sm tracking-tight">Readiness Dimensions <ScoreInfo scoreId="gmari" /></p>
          </div>
          {g.dimensions.map((dim) => (
            <div key={dim.id} data-testid={`dimension-${dim.id}`} className="px-5 py-3 border-b border-slate-100 last:border-0">
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs font-semibold text-slate-800 w-64 shrink-0">{dim.label}
                  {dim.weight > 0 && <span className="text-slate-400 font-normal ml-1.5">({dim.weight}%)</span>}
                </p>
                <div className="flex-1 h-2 bg-slate-100">
                  {dim.score !== null && <div className="h-full" style={{ width: `${dim.score}%`, background: dim.score >= 70 ? "#047857" : dim.score >= 50 ? "#B45309" : "#B91C1C" }} />}
                </div>
                <p className="font-mono-data text-sm font-bold w-14 text-right shrink-0">
                  {dim.score !== null ? dim.score : <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Pending</span>}
                </p>
              </div>
              <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{dim.detail}</p>
            </div>
          ))}
        </div>
      </div>

      <div data-testid="readiness-columns" className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {VERDICT_META.map((v) => {
          const markets = d.country_readiness.filter((c) => c.wave === v.key);
          return (
            <div key={v.key} data-testid={`readiness-col-${v.key}`} className="bg-white border border-slate-200" style={{ borderTop: `3px solid ${v.color}` }}>
              <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
                <v.icon size={16} weight="bold" style={{ color: v.color }} />
                <p className="font-heading font-bold text-sm">{v.label}</p>
                <ScoreInfo scoreId="country_readiness" />
                <span className="ml-auto font-mono-data text-xs text-slate-400">{markets.length}</span>
              </div>
              <div className="divide-y divide-slate-50">
                {markets.map((c) => (
                  <div key={c.id} data-testid={`readiness-market-${c.id}`} className="px-5 py-2.5">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-800">{c.flag} {c.name}</p>
                      <p className="font-mono-data text-xs font-bold" style={{ color: v.color }}>{c.readiness_score}</p>
                    </div>
                    <p className="font-mono-data text-[10px] text-slate-400 mt-0.5">
                      {c.requirements_satisfied} met · {c.requirements_partial} partial · {c.requirements_missing} missing · ~{c.months_to_decision}mo
                      {c.top_blocker ? ` · blocker: ${c.top_blocker}` : ""}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <DomainReadinessSection productId={productId} />

      <section data-testid="next-actions" className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <p className="font-heading font-bold text-sm tracking-tight">Recommended Next Actions</p>
            <p className="text-xs text-slate-500 mt-1">Highest-impact evidence investments to raise the index — simulate them live in Scenario Studio.</p>
          </div>
          <button data-testid="open-scenario-btn" onClick={() => navigate("/console/whatif")}
            className="text-xs font-semibold text-slate-900 border border-slate-300 px-3 py-2 hover:bg-slate-50 flex items-center gap-1.5">
            Scenario Studio <ArrowRight size={12} weight="bold" />
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {d.next_actions.map((a, i) => (
            <div key={i} className="px-5 py-3 flex items-start gap-4">
              <span className="font-mono-data text-[10px] text-slate-300 mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <p className="text-xs font-semibold text-slate-800">{a.action}
                  <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-slate-200 text-slate-400">{a.markets_impacted} market(s)</span>
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{a.rationale}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
